import { createPortal } from 'react-dom';
import { forwardRef, useEffect, useId, useImperativeHandle, useRef, useState } from 'react';
import { combineClassNames } from '../../Utils/ClassnameUtils';
import GWPButton from './GWPButton';
import GWPModalContext from './GWPModalContext';
import {
	getGWPFocusableElements,
	getGWPInitialFocusTarget,
	getGWPOverlayRoot,
	registerGWPOverlay,
	unregisterGWPOverlay,
} from './GWPOverlayUtils';

export type GWPModalSize = 'sm' | 'md' | 'lg' | 'xl' | 'full';
export type GWPModalRole = 'dialog' | 'alertdialog';
export type GWPModalPosition = 'center' | 'top';
const MODAL_EXIT_DURATION_MS = 180;

export interface GWPModalProps {
	children: React.ReactNode;
	open: boolean;
	onClose?: () => void;
	size?: GWPModalSize;
	role?: GWPModalRole;
	position?: GWPModalPosition;
	className?: string;
	closeOnBackdrop?: boolean;
	closeOnEscape?: boolean;
	showCloseButton?: boolean;
	busy?: boolean;
	initialFocusRef?: React.RefObject<HTMLElement>;
	restoreFocusRef?: React.RefObject<HTMLElement>;
	label?: string;
	labelledBy?: string;
	describedBy?: string;
	topOffset?: number | string;
	zIndex?: number;
}

const GWPModal = forwardRef<HTMLDivElement, GWPModalProps>((props, ref) => {
	const {
		children,
		open,
		onClose,
		size = 'md',
		role = 'dialog',
		position = 'center',
		className,
		closeOnBackdrop = false,
		closeOnEscape = true,
		showCloseButton = true,
		busy = false,
		initialFocusRef,
		restoreFocusRef,
		label,
		labelledBy,
		describedBy,
		topOffset,
		zIndex,
	} = props;

	const generatedId = useId().replace(/:/g, '');
	const titleId = `gwp-modal-title-${generatedId}`;
	const descriptionId = `gwp-modal-description-${generatedId}`;
	const [portalElement, setPortalElement] = useState<HTMLDivElement | null>(null);
	const [isRendered, setIsRendered] = useState(open);
	const [isActive, setIsActive] = useState(false);
	const [isClosing, setIsClosing] = useState(false);
	const [hasTitle, setHasTitle] = useState(false);
	const [hasDescription, setHasDescription] = useState(false);
	const internalSurfaceRef = useRef<HTMLDivElement>(null);
	const lastFocusedElementRef = useRef<HTMLElement | null>(null);

	useImperativeHandle(ref, () => internalSurfaceRef.current as HTMLDivElement);

	useEffect(() => {
		if (!isRendered || typeof document === 'undefined') {
			return undefined;
		}

		setPortalElement(getGWPOverlayRoot());
		return undefined;
	}, [isRendered]);

	useEffect(() => {
		if (open) {
			if (!isRendered) {
				lastFocusedElementRef.current =
					document.activeElement instanceof HTMLElement ? document.activeElement : null;
				setIsRendered(true);
			}

			return undefined;
		}

		if (!isRendered) {
			setIsActive(false);
			setIsClosing(false);
			return undefined;
		}

		setIsClosing(true);
		setIsActive(false);
		const timeout = setTimeout(() => {
			setIsClosing(false);
			setIsRendered(false);
			const restoreTarget = restoreFocusRef?.current ?? lastFocusedElementRef.current;
			restoreTarget?.focus({ preventScroll: true });
		}, MODAL_EXIT_DURATION_MS);

		return () => {
			clearTimeout(timeout);
		};
	}, [isRendered, open, restoreFocusRef]);

	useEffect(() => {
		if (!open || !isRendered || portalElement === null || !portalElement.isConnected) {
			return undefined;
		}

		let firstFrame = 0;
		let secondFrame = 0;

		setIsClosing(false);
		setIsActive(false);

		firstFrame = window.requestAnimationFrame(() => {
			secondFrame = window.requestAnimationFrame(() => {
				setIsActive(true);
			});
		});

		return () => {
			window.cancelAnimationFrame(firstFrame);
			window.cancelAnimationFrame(secondFrame);
		};
	}, [isRendered, open, portalElement]);

	useEffect(() => {
		if (!isRendered || portalElement === null || !portalElement.isConnected) {
			return undefined;
		}

		registerGWPOverlay();

		return () => {
			unregisterGWPOverlay();
		};
	}, [isRendered, portalElement]);

	useEffect(() => {
		if (!open || !isActive || portalElement === null || !portalElement.isConnected) {
			return undefined;
		}

		const timeout = window.setTimeout(() => {
			const focusTarget = getGWPInitialFocusTarget(
				internalSurfaceRef.current,
				initialFocusRef?.current,
				internalSurfaceRef.current,
			);
			focusTarget?.focus({ preventScroll: true });
		}, 0);

		return () => {
			window.clearTimeout(timeout);
		};
	}, [initialFocusRef, isActive, open, portalElement]);

	const handleClose = () => {
		if (busy) {
			return;
		}

		onClose?.();
	};

	const handleOverlayClick = (event: React.MouseEvent<HTMLDivElement>) => {
		if (!closeOnBackdrop || event.target !== event.currentTarget) {
			return;
		}

		handleClose();
	};

	const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
		if (event.key === 'Escape' && closeOnEscape) {
			event.stopPropagation();
			handleClose();
			return;
		}

		if (event.key !== 'Tab') {
			return;
		}

		const focusableElements = getGWPFocusableElements(internalSurfaceRef.current);

		if (focusableElements.length === 0) {
			event.preventDefault();
			internalSurfaceRef.current?.focus();
			return;
		}

		const firstFocusable = focusableElements[0];
		const lastFocusable = focusableElements[focusableElements.length - 1];
		const activeElement = document.activeElement;

		if (event.shiftKey && activeElement === firstFocusable) {
			event.preventDefault();
			lastFocusable?.focus();
			return;
		}

		if (!event.shiftKey && activeElement === lastFocusable) {
			event.preventDefault();
			firstFocusable?.focus();
		}
	};

	if (!isRendered || portalElement === null || !portalElement.isConnected) {
		return null;
	}

	const resolvedLabelledBy = label ? undefined : (labelledBy ?? (hasTitle ? titleId : undefined));
	const resolvedDescribedBy = describedBy ?? (hasDescription ? descriptionId : undefined);
	const surfaceClassName = combineClassNames('gwp-modal-surface', size, className);
	const overlayClassName = combineClassNames(
		'gwp-modal-overlay',
		position === 'top' ? 'position-top' : undefined,
		isActive ? 'open' : undefined,
		isClosing ? 'closing' : undefined,
	);
	const overlayStyle = {
		'--gwp-modal-top-offset':
			typeof topOffset === 'number' ? `${topOffset}px` : (topOffset ?? 'var(--gwp-app-chrome-offset, 0px)'),
		'--gwp-modal-z-index': zIndex !== undefined ? String(zIndex) : undefined,
	} as React.CSSProperties;

	const modal = (
		<GWPModalContext.Provider
			value={{
				titleId,
				descriptionId,
				registerTitle: setHasTitle,
				registerDescription: setHasDescription,
			}}
		>
			<div className={overlayClassName} style={overlayStyle} onClick={handleOverlayClick}>
				<div
					ref={internalSurfaceRef}
					className={surfaceClassName}
					role={role}
					aria-modal="true"
					aria-label={label}
					aria-labelledby={resolvedLabelledBy}
					aria-describedby={resolvedDescribedBy}
					tabIndex={-1}
					onKeyDown={handleKeyDown}
				>
					{children}
					{showCloseButton && (
						<GWPButton
							className="gwp-modal-close"
							variant="ghost"
							tone="neutral"
							size="sm"
							icon="fas fa-times"
							onClick={handleClose}
							disabled={busy}
							aria-label="Close dialog"
							title="Close dialog"
							data-gwp-overlay-close="true"
						/>
					)}
				</div>
			</div>
		</GWPModalContext.Provider>
	);

	return createPortal(modal, portalElement);
});

GWPModal.displayName = 'GWPModal';

export default GWPModal;
